import { useState } from 'react'
import { CarrierConnections } from './CarrierConnections'

type Mode = 'manual' | 'sheet' | 'carrier' | 'partner'

export interface IntakeRate {
  provider: string
  sourceType: 'line' | 'partner' | 'spot'
  origin: string
  destination: string
  equipment: string
  amount: number
  currency: string
  validUntil: string
}

export function RateIntake({ onAdd }: { onAdd: (rate: IntakeRate) => void }) {
  const [mode, setMode] = useState<Mode | null>(null)
  const [saved, setSaved] = useState('')
  const [fileName, setFileName] = useState('')
  const [provider, setProvider] = useState('')
  const [endpoint, setEndpoint] = useState('')
  const [connected, setConnected] = useState<string[]>([])
  const [manual, setManual] = useState<IntakeRate>({
    provider: '',
    sourceType: 'line',
    origin: '',
    destination: '',
    equipment: '40HC',
    amount: 0,
    currency: 'USD',
    validUntil: '2026-10-31',
  })

  const addManual = () => {
    if (!manual.provider || !manual.origin || !manual.destination || !manual.amount) return
    onAdd(manual)
    setSaved(`${manual.provider} rate added`)
    setMode(null)
  }
  const connect = () => {
    if (!provider || !endpoint) return
    setConnected((current) => [...current, provider])
    setSaved(`${provider} connected and syncing`)
    setProvider('')
    setEndpoint('')
    setMode(null)
  }

  return (
    <section className="rti">
      <header>
        <div>
          <span>Rate intake</span>
          <h3>Add rates without changing your workflow.</h3>
          <p>One clean rate model, whichever source sends the price.</p>
        </div>
        {saved && <b>{saved} ✓</b>}
      </header>
      <div className="rti-paths">
        <Path
          icon="＋"
          title="Add one rate"
          detail="Enter a carrier, lane and price manually."
          active={mode === 'manual'}
          onClick={() => setMode(mode === 'manual' ? null : 'manual')}
        />
        <Path
          icon="⇧"
          title="Import spreadsheet"
          detail="XLSX, XLS or CSV with reusable mapping."
          active={mode === 'sheet'}
          onClick={() => setMode(mode === 'sheet' ? null : 'sheet')}
        />
        <Path
          icon="↔"
          title="Connect carrier"
          detail="API, webhook, SFTP or scheduled endpoint."
          active={mode === 'carrier'}
          onClick={() => setMode(mode === 'carrier' ? null : 'carrier')}
        />
        <Path
          icon="◎"
          title="Partner feed"
          detail="Receive agent and co-loader tariffs."
          active={mode === 'partner'}
          onClick={() => setMode(mode === 'partner' ? null : 'partner')}
        />
      </div>

      {mode === 'manual' && (
        <div className="rti-panel rti-manual">
          <Field label="Provider">
            <input
              aria-label="Rate provider"
              value={manual.provider}
              onChange={(e) => setManual({ ...manual, provider: e.target.value })}
              placeholder="MSC"
            />
          </Field>
          <Field label="Source">
            <select
              aria-label="Rate source type"
              value={manual.sourceType}
              onChange={(e) =>
                setManual({ ...manual, sourceType: e.target.value as IntakeRate['sourceType'] })
              }
            >
              <option value="line">Line contract</option>
              <option value="partner">Partner rate</option>
              <option value="spot">Spot offer</option>
            </select>
          </Field>
          <Field label="Origin">
            <input
              aria-label="Rate origin"
              value={manual.origin}
              onChange={(e) => setManual({ ...manual, origin: e.target.value.toUpperCase() })}
              placeholder="CNSHA"
            />
          </Field>
          <Field label="Destination">
            <input
              aria-label="Rate destination"
              value={manual.destination}
              onChange={(e) => setManual({ ...manual, destination: e.target.value.toUpperCase() })}
              placeholder="NLRTM"
            />
          </Field>
          <Field label="Equipment">
            <select
              aria-label="Rate equipment"
              value={manual.equipment}
              onChange={(e) => setManual({ ...manual, equipment: e.target.value })}
            >
              <option>20GP</option>
              <option>40GP</option>
              <option>40HC</option>
              <option>40' FR</option>
              <option>40' OT</option>
            </select>
          </Field>
          <Field label="Buy rate">
            <div className="rti-money">
              <select
                aria-label="Rate currency"
                value={manual.currency}
                onChange={(e) => setManual({ ...manual, currency: e.target.value })}
              >
                <option>USD</option>
                <option>EUR</option>
                <option>GBP</option>
              </select>
              <input
                aria-label="Rate amount"
                type="number"
                value={manual.amount || ''}
                onChange={(e) => setManual({ ...manual, amount: Number(e.target.value) })}
                placeholder="0"
              />
            </div>
          </Field>
          <Field label="Valid until">
            <input
              aria-label="Rate valid until"
              type="date"
              value={manual.validUntil}
              onChange={(e) => setManual({ ...manual, validUntil: e.target.value })}
            />
          </Field>
          <button type="button" className="rti-primary" onClick={addManual}>
            Add usable rate →
          </button>
        </div>
      )}

      {mode === 'sheet' && (
        <div className="rti-panel rti-sheet">
          <label className="rti-drop">
            <input
              aria-label="Import rate spreadsheet"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')}
            />
            <i>⇧</i>
            <b>{fileName || 'Drop a rate sheet here'}</b>
            <span>XLSX, XLS or CSV · up to 20 MB</span>
          </label>
          <div className="rti-map">
            <span>We map</span>
            <b>POL → Origin</b>
            <b>POD → Destination</b>
            <b>40HC → Buy rate</b>
            <b>Validity → Valid until</b>
            <button type="button">Edit mapping</button>
          </div>
          <button
            type="button"
            className="rti-primary"
            disabled={!fileName}
            onClick={() => {
              setSaved(`${fileName} imported: 24 rates ready`)
              setMode(null)
            }}
          >
            Validate and import →
          </button>
        </div>
      )}

      {mode === 'carrier' && (
        <CarrierConnections
          onConnected={(name) => {
            setConnected((current) => (current.includes(name) ? current : [...current, name]))
            setSaved(`${name} verified for sandbox use`)
          }}
          onMinimize={() => setMode(null)}
        />
      )}

      {mode === 'partner' && (
        <div className="rti-panel rti-connect">
          <div>
            <Field label="Partner or agent">
              <input
                aria-label="Connection provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder="SeaBridge GmbH"
              />
            </Field>
            <Field label="Connection method">
              <select aria-label="Connection method">
                <option>REST API</option>
                <option>Webhook</option>
                <option>SFTP folder</option>
                <option>Email attachment inbox</option>
              </select>
            </Field>
            <Field label="Endpoint">
              <input
                aria-label="Rate endpoint"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder="https://api.provider.com/rates"
              />
            </Field>
            <Field label="Authentication">
              <select aria-label="Endpoint authentication">
                <option>API key</option>
                <option>OAuth 2.0</option>
                <option>Basic auth</option>
                <option>None</option>
              </select>
            </Field>
          </div>
          <aside>
            <span>Simple setup</span>
            <ol>
              <li>Test connection</li>
              <li>Map rate fields</li>
              <li>Choose sync schedule</li>
              <li>Review before publishing</li>
            </ol>
            <button type="button" className="rti-primary" onClick={connect}>
              Test and connect →
            </button>
          </aside>
        </div>
      )}

      <footer>
        <span>Connected sources</span>
        {connected.length === 0 && <small>No live carrier connections yet</small>}
        {connected.map((item) => (
          <b key={item}>
            <i />
            {item}
            <small>Healthy · synced 8 min ago</small>
          </b>
        ))}
        <button type="button" onClick={() => setMode('carrier')}>
          Manage connections →
        </button>
      </footer>
      <style>{styles}</style>
    </section>
  )
}

function Path({
  icon,
  title,
  detail,
  active,
  onClick,
}: { icon: string; title: string; detail: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={active ? 'active' : ''} onClick={onClick}>
      <i>{icon}</i>
      <span>
        <b>{title}</b>
        <small>{detail}</small>
      </span>
      <em>→</em>
    </button>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rti-field">
      <span>{label}</span>
      {children}
    </div>
  )
}

const styles = `
.rti{border:1px solid var(--border);background:var(--surface-0);border-radius:14px;margin-top:14px;overflow:hidden;box-shadow:var(--shadow-card)}.rti *{box-sizing:border-box}.rti button,.rti input,.rti select{font:inherit}.rti>header{display:flex;justify-content:space-between;align-items:center;padding:13px 15px;border-bottom:1px solid var(--border)}.rti>header span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.rti>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:17px;margin:2px 0}.rti>header p{font-size:8.5px;color:var(--text-muted);margin:0}.rti>header>b{font-size:8.5px;color:#047857}.rti-paths{display:grid;grid-template-columns:repeat(4,1fr)}.rti-paths>button{display:grid;grid-template-columns:30px 1fr auto;gap:8px;align-items:center;border:0;border-right:1px solid var(--border);background:transparent;color:inherit;text-align:left;padding:12px;cursor:pointer}.rti-paths>button:last-child{border:0}.rti-paths>button.active{background:var(--surface-1);box-shadow:inset 0 -2px var(--frd-accent)}.rti-paths i{width:28px;height:28px;border:1px solid var(--border);border-radius:8px;display:grid;place-items:center;font-style:normal;color:var(--frd-accent)}.rti-paths b,.rti-paths small{display:block}.rti-paths b{font-size:9.5px}.rti-paths small{font-size:7.5px;color:var(--text-muted);margin-top:2px;line-height:1.35}.rti-paths em{font-style:normal;color:var(--text-muted)}.rti-panel{border-top:1px solid var(--border);padding:14px;background:var(--surface-1)}.rti-manual{display:grid;grid-template-columns:1.2fr .9fr .8fr .8fr .7fr 1fr .9fr auto;gap:8px;align-items:end}.rti-field{display:grid;gap:4px}.rti-field>span{font-family:var(--mono,ui-monospace,monospace);font-size:7px;text-transform:uppercase;color:var(--text-muted)}.rti-field input,.rti-field select{width:100%;border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px;font-size:9px;min-width:0}.rti-money{display:grid;grid-template-columns:60px 1fr}.rti-money select{border-radius:8px 0 0 8px}.rti-money input{border-radius:0 8px 8px 0;border-left:0}.rti-primary{border:0;background:var(--text-primary);color:var(--surface-0);border-radius:8px;padding:9px 11px;font-size:8.5px;font-weight:700;white-space:nowrap}.rti-primary:disabled{opacity:.35}.rti-sheet{display:grid;grid-template-columns:1.1fr 2fr auto;gap:12px;align-items:center}.rti-drop{position:relative;display:grid;grid-template-columns:26px 1fr;gap:2px 8px;align-items:center;border:1px dashed var(--border);border-radius:9px;padding:10px;background:var(--surface-0)}.rti-drop input{position:absolute;inset:0;opacity:0;cursor:pointer}.rti-drop i{grid-row:1/3;width:26px;height:26px;border-radius:7px;background:var(--surface-1);display:grid;place-items:center;font-style:normal;color:var(--frd-accent)}.rti-drop b{font-size:9px}.rti-drop span{font-size:7.5px;color:var(--text-muted)}.rti-map{display:flex;align-items:center;gap:6px;flex-wrap:wrap}.rti-map span{font-size:8px;color:var(--text-muted)}.rti-map b{font-family:var(--mono,ui-monospace,monospace);font-size:7.5px;border:1px solid var(--border);background:var(--surface-0);border-radius:6px;padding:5px 6px}.rti-map button{border:0;background:none;color:var(--frd-accent);font-size:8px;font-weight:700}.rti-connect{display:grid;grid-template-columns:1fr 220px;gap:16px}.rti-connect>div{display:grid;grid-template-columns:1fr 1fr;gap:9px}.rti-connect aside{border-left:1px solid var(--border);padding-left:15px}.rti-connect aside>span{font-family:var(--mono,ui-monospace,monospace);font-size:7px;text-transform:uppercase;color:var(--frd-accent)}.rti-connect ol{padding-left:16px;margin:7px 0 10px;font-size:8px;line-height:1.7;color:var(--text-muted)}.rti>footer{display:flex;align-items:center;gap:10px;padding:10px 14px;border-top:1px solid var(--border);font-size:8px}.rti>footer>span{font-family:var(--mono,ui-monospace,monospace);text-transform:uppercase;color:var(--text-muted)}.rti>footer>b{display:flex;align-items:center;gap:5px}.rti>footer>b>i{width:6px;height:6px;border-radius:50%;background:#10b981}.rti>footer small{font-weight:400;color:var(--text-muted)}.rti>footer button{margin-left:auto;border:0;background:none;color:var(--frd-accent);font-size:8px;font-weight:700}
@media(max-width:900px){.rti-paths{grid-template-columns:1fr 1fr}.rti-paths>button{border-bottom:1px solid var(--border)}.rti-manual{grid-template-columns:1fr 1fr}.rti-sheet{grid-template-columns:1fr}.rti-connect{grid-template-columns:1fr}.rti-connect aside{border-left:0;border-top:1px solid var(--border);padding:10px 0 0}}
@media(max-width:560px){.rti-paths{grid-template-columns:1fr}.rti-paths>button{border-right:0}.rti-manual,.rti-connect>div{grid-template-columns:1fr}.rti>footer{align-items:flex-start;flex-direction:column}.rti>footer button{margin-left:0}}
`
